import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { SelectObject } from '@components/SelectObject'
import { SelectString } from '@components/SelectString'
import type { Gebied } from '@models/Gebied'
import type { Projectbetrokkene } from '@models/Projectbetrokkene'

import type { Discipline } from '../../../core/util/discipline'
import { StyledError } from '../../../theme/components/StyledError'
import { useCreateProjectbetrokkene } from '../../hooks/useCreateProjectbetrokkene'

import { SelectDisciplines } from './SelectDisciplines'

export { CreateVerantwoordelijke }

type Props = {
  onClose: () => void
  gebieden: Gebied[]
  projectType: Projectbetrokkene['projectType']
}

const verantwoordelijkeTypes: Record<
  Projectbetrokkene['projectType'],
  Projectbetrokkene['type'][]
> = {
  Aanvraag: ['Combi'],
  Hoofdleiding: ['Combi', 'Netbeheerder'],
}

const CreateVerantwoordelijke = ({ onClose, gebieden, projectType }: Props) => {
  const { mutate: createProjectbetrokkene, error } =
    useCreateProjectbetrokkene()

  const types = verantwoordelijkeTypes[projectType]

  const [verantwoordelijke, setVerantwoordelijke] = useState('')
  const [gebied, setGebied] = useState<Gebied | null>(null)
  const [type, setType] = useState<Projectbetrokkene['type']>(types[0])
  const [disciplines, setDisciplines] = useState<Discipline[]>([])
  const [errorText, setErrorText] = useState('')
  const [disciplinesErrorText, setDisciplinesErrorText] = useState('')

  const handleSubmit = () => {
    if (!verantwoordelijke) {
      setErrorText('Verantwoordelijke is verplicht')

      return
    }

    if (!gebied) {
      setErrorText('Gebied is verplicht')

      return
    }

    if (type === 'Netbeheerder' && disciplines.length === 0) {
      setDisciplinesErrorText('Minimaal één discipline is verplicht')

      return
    }

    createProjectbetrokkene(
      {
        projectbetrokkene: {
          projectType,
          verantwoordelijke,
          gebiedscode: gebied.code,
          gebiedscodeLabel: '',
          organisatie: verantwoordelijke,
          type,
          ...(type === 'Netbeheerder' ? { disciplines } : {}),
        },
      },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>Verantwoordelijke toevoegen</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SelectObject
          options={gebieden}
          label="Gebied"
          value={gebied}
          onChange={(value) => {
            if (value) {
              setGebied(value)
            }
          }}
        />

        <TextField
          label="Verantwoordelijke"
          value={verantwoordelijke}
          onChange={(e) => setVerantwoordelijke(e.target.value)}
          variant="standard"
          error={!!errorText}
          helperText={errorText}
        />

        <SelectString
          label="Type"
          options={types}
          value={type}
          onChange={(type) => setType(type)}
          disableClearable
        />

        {type === 'Netbeheerder' && (
          <SelectDisciplines
            projectType={projectType}
            value={disciplines}
            onChange={(value) => {
              setDisciplines(value)
              setDisciplinesErrorText('')
            }}
            errorText={disciplinesErrorText}
          />
        )}

        <StyledError error={error} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} autoFocus>
          Sluiten
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          endIcon={<AddIcon />}
        >
          Toevoegen
        </Button>
      </DialogActions>
    </Dialog>
  )
}
